import React, { useMemo } from "react";
import { EmailPreview } from "@components/Text";
import { useOutletContext } from "react-router-dom";
import { getOrganizationFooterHtml } from "@components/Organization";

import renderContent from "./helpers/renderContent";
import type { Context, Organization, Project, TagsMap } from "./helpers/renderContent";

type Account = Record<string, unknown>;

type OutletOrganization = Organization & {
  emailFooterHtml?: string;
};

type OutletContext = {
  project: Project;
  accountsMap: Record<string, Account>;
  organization: OutletOrganization;
};

type PreviewContext = Context & {
  account?: Account;
};

type Props = {
  variables?: string[];
  extraContext?: Partial<Context>;
  htmlTemplate?: string;
  subjectTemplate?: string;
};

const VARIABLE_SEPARATOR = ":";

const EmailTemplatePreview = ({
  variables = undefined,
  extraContext = undefined,
  htmlTemplate = "",
  subjectTemplate = "",
}: Props): JSX.Element => {
  const { project, accountsMap, organization } = useOutletContext<OutletContext>();

  const { emailFooterHtml: footerHtml } = organization;

  const tagsMap = useMemo<TagsMap>(() =>
    (variables || [])
      .sort((a, b) => a.localeCompare(b))
      .reduce((acc, pair) => {
        const [ variable, tag ] = pair.split(VARIABLE_SEPARATOR);

        return { ...acc, [variable]: tag };
      }, {})
  , [ variables ]);

  const [ account ] = Object.values(accountsMap);

  const context: PreviewContext = {
    project,
    organization,
    account,
    tagsMap,
    ...extraContext,
  };

  const resolvedFooterHtml = footerHtml || getOrganizationFooterHtml(organization);
  const hasResolvedFooterHtml = Boolean(resolvedFooterHtml);

  const html = hasResolvedFooterHtml
    ? `${htmlTemplate}\n${resolvedFooterHtml}`
    : htmlTemplate;

  const renderedHtml = renderContent(html, context, false, variables) || "";
  const renderedSubject = renderContent(subjectTemplate, context, true, variables) || "";

  const emailHtml = `
    <h3 style="margin-bottom: 16px">
      ${renderedSubject}
    </h3>
    ${renderedHtml}
  `;

  return (
    <EmailPreview html={emailHtml} />
  );
};

export default EmailTemplatePreview;
